import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, 
  ChevronLeft,
  ChevronRight,
  Clock,
  Search,
  Filter,
  ChevronDown
} from 'lucide-react';
import api from '../../connector/api';
import UniformInfractionModal from '../components/UniformInfractionModal'; 
import StudentUniformDetail from './StudentUniformDetail';
import DeleteConfirmModal from '../components/DeleteConfirmationModal';
import StudentListLayout from '../components/layout/StudentListLayout';

export default function UniformInfractionPage() {
    const [currentUser, setCurrentUser] = useState(null);
    const [infractions, setInfractions] = useState([]);
    const [schoolYears, setSchoolYears] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [violationFilter, setViolationFilter] = useState('');
    const [schoolYearFilter, setSchoolYearFilter] = useState('');
    const [termFilter, setTermFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    
    // View state for detail drill-down
    const [selectedStudentId, setSelectedStudentId] = useState(null);

    // Modal Interaction States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInfraction, setSelectedInfraction] = useState(null);

    // Delete Modal & Deletion States
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [infractionToDelete, setInfractionToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Pagination Meta States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);
    const [totalItems, setTotalItems] = useState(0);

    // Filter Dropdown Toggles & Click Ref
    const [isViolationDropdownOpen, setIsViolationDropdownOpen] = useState(false);
    const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsViolationDropdownOpen(false);
                setIsYearDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchUserData = async () => {
            try {
                const response = await api.get('accounts/me/');
                if (!isMounted) return;
                
                if (response.data && response.data.user) {
                    setCurrentUser({
                        ...response.data.user,
                        permissions: response.data.permissions || []
                    });
                } else if (response.data) {
                    setCurrentUser(response.data);
                }
            } catch (error) {
                console.error("Could not load user data from backend API:", error);
            }
        };

        if (!currentUser) {
            fetchUserData();
        }

        return () => { isMounted = false; };
    }, [currentUser]);
    
    const getViolationText = (log) => {
        if (!log) return "N/A";
        if (log.specific_item && typeof log.specific_item === 'string') return log.specific_item;
        if (log.specific_item && typeof log.specific_item === 'object') {
            return log.specific_item.name || log.specific_item.label || log.specific_item.title || "N/A";
        }
        if (typeof log.violation === 'string' && log.violation.trim()) return log.violation;
        if (log.violation && typeof log.violation === 'object') {
            return log.violation.name || log.violation.label || log.violation.title || "N/A";
        }
        if (typeof log.violation_type === 'string' && log.violation_type.trim()) return log.violation_type;
        if (log.violation_type && typeof log.violation_type === 'object') {
            return log.violation_type.name || log.violation_type.label || log.violation_type.title || "N/A";
        }
        if (log.specific_item_display) return log.specific_item_display;
        if (log.infraction_type) return log.infraction_type;
        return "N/A";
    };

    useEffect(() => {
        const fetchSchoolYears = async () => {
            try {
                const response = await api.get('/academic/years/');
                const data = response.data.results || response.data || [];
                setSchoolYears(data);
            } catch (error) {
                console.error("Error retrieving academic school years:", error);
            }
        };
        fetchSchoolYears();
    }, []);

    // Fetch routine aligned with Django View parameters
    const fetchInfractions = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {
                page: currentPage,
                page_size: itemsPerPage,
            };

            if (searchTerm.trim()) params.search = searchTerm.trim();
            if (violationFilter) params.violation = violationFilter;
            if (schoolYearFilter) {
                params.academic_year = schoolYearFilter;
                params.school_year_id = schoolYearFilter;
            }
            if (termFilter) params.term = termFilter;
            if (dateFilter) params.date = dateFilter;

            const response = await api.get('/infraction-reports/', { params });
            
            if (response.data) {
                const results = response.data.results || (Array.isArray(response.data) ? response.data : []);
                setInfractions(results);
                setTotalItems(response.data.count ?? results.length);
            }
        } catch (error) {
            console.error("Error retrieving infraction logs:", error);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, searchTerm, violationFilter, schoolYearFilter, termFilter, dateFilter]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchInfractions();
        }, 350);
        return () => clearTimeout(delayDebounce);
    }, [fetchInfractions]);

    const handleSaveInfraction = async (payload, infractionId) => {
        try {
            const targetId = infractionId || selectedInfraction?.id;

            if (targetId) {
                await api.patch(`/infraction-reports/${targetId}/`, payload);
            } else {
                await api.post('/infraction-reports/', payload);
            }
            
            await fetchInfractions();
            setIsModalOpen(false);
            setSelectedInfraction(null);
            return true; 
        } catch (error) {
            console.error("Error committing infraction modification:", error);
            throw error; 
        }
    };

    const handleConfirmDelete = async () => {
        if (!infractionToDelete) return;

        setIsDeleting(true);
        try {
            await api.delete(`/infraction-reports/${infractionToDelete.id}/`);
            await fetchInfractions();
            setIsDeleteModalOpen(false);
            setInfractionToDelete(null);
        } catch (error) {
            console.error("Error deleting infraction record:", error);
            alert("Failed to delete record. Please check administrative permissions.");
        } finally {
            setIsDeleting(false);
        }
    };

    const openCreateModal = (initialData = null) => {
        setSelectedInfraction(initialData);
        setIsModalOpen(true);
    };

    const formatInfractionDateTime = (dateString) => {
        if (!dateString) return { date: '—', time: '' };
        const dateObj = new Date(dateString);
        return {
            date: dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
            time: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
    };

    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    const getSelectedYearLabel = () => {
        if (!schoolYearFilter) return "All Academic Years";
        const found = schoolYears.find(sy => String(sy.id) === String(schoolYearFilter) || sy.name === schoolYearFilter);
        return found ? (found.name || found.year_label) : schoolYearFilter;
    };

    return (
        <StudentListLayout>
            <div className="p-6 max-w-[1400px] w-full mx-auto" ref={dropdownRef}>
                {selectedStudentId ? (
                    <StudentUniformDetail 
                        studentId={selectedStudentId}
                        onBack={() => setSelectedStudentId(null)}
                        currentUser={currentUser}
                        onOpenModal={openCreateModal}
                        onRefreshNeeded={fetchInfractions}
                    />
                ) : (
                    <div className="bg-white rounded-lg border border-slate-200/80 shadow-xs overflow-hidden">
                        
                        <div className="p-5 border-b border-slate-100 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-[13px] font-semibold text-slate-900">Uniform Infractions</h1>
                                    <p className="text-[11px] text-slate-500 font-medium">
                                        Log, cross-reference, and filter dress-code policy deviations systematically.
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-2.5">
                                    {/* Search Input */}
                                    <div className="relative w-full sm:w-60">
                                        <Search className="absolute left-3 top-2 text-slate-400" size={15} />
                                        <input
                                            type="text"
                                            placeholder="Search student, classroom..."
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                        />
                                    </div>

                                    {/* School Year Filter */}
                                    {schoolYears.length > 0 && (
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsYearDropdownOpen(!isYearDropdownOpen);
                                                    setIsViolationDropdownOpen(false);
                                                }}
                                                className="flex items-center gap-2 bg-slate-50 border font-sans border-slate-200 px-3 py-1.5 rounded-xl text-[12px] font-semibold text-slate-700 hover:bg-slate-100/80 transition-colors cursor-pointer"
                                            >
                                                <Filter size={13} className="text-slate-400" />
                                                <span className="font-semibold text-slate-800">
                                                    {getSelectedYearLabel()}
                                                </span>
                                                <ChevronDown size={13} className="text-slate-400" />
                                            </button>

                                            {isYearDropdownOpen && (
                                                <div className="absolute right-0 mt-1.5 w-48 font-sans bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSchoolYearFilter('');
                                                            setCurrentPage(1);
                                                            setIsYearDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                                                            !schoolYearFilter ? "bg-blue-50 font-bold text-blue-600" : "text-slate-700 hover:bg-slate-50"
                                                        }`}
                                                    >
                                                        All Academic Years
                                                    </button>
                                                    {schoolYears.map((sy) => {
                                                        const syValue = sy.id || sy.name;
                                                        const isSelected = String(schoolYearFilter) === String(syValue);
                                                        return (
                                                            <button
                                                                key={sy.id || sy.name}
                                                                type="button"
                                                                onClick={() => {
                                                                    setSchoolYearFilter(syValue);
                                                                    setCurrentPage(1);
                                                                    setIsYearDropdownOpen(false);
                                                                }}
                                                                className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                                                                    isSelected ? "bg-blue-50 font-bold text-blue-600" : "text-slate-700 hover:bg-slate-50"
                                                                }`}
                                                            >
                                                                {sy.name || sy.year_label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => openCreateModal(null)}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-sans font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-2xs cursor-pointer"
                                    >
                                        <Plus size={15} /> File Infraction
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] min-h-[300px] custom-scroll">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="sticky top-0 z-10 bg-slate-50 border-b border-slate-100 text-[10px] uppercase font-semibold text-slate-400 tracking-wider shadow-2xs">
                                        <th className="py-3 px-4 bg-slate-50">Student Name</th>
                                        <th className="py-3 px-4 bg-slate-50">Latest Violation</th>
                                        <th className="py-3 px-4 bg-slate-50 text-center">Total Violations</th>
                                        <th className="py-3 px-4 bg-slate-50">Logged Time</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-600">
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="4" className="text-center py-10 text-slate-400 text-xs font-medium">
                                                Loading infraction reports...
                                            </td>
                                        </tr>
                                    ) : infractions.length > 0 ? (
                                        infractions.map((log) => {
                                            const dateTime = formatInfractionDateTime(log.date_of_infraction || log.created_at || log.date);
                                            const violationDisplay = getViolationText(log);
                                            const totalCount = log.total_infractions || 1;

                                            return (
                                                <tr 
                                                    key={log.id}
                                                    onClick={() => setSelectedStudentId(log.student_id || log.student)}
                                                    className="hover:bg-slate-50/60 transition-colors cursor-pointer"
                                                >
                                                    <td className="py-3.5 px-4 font-bold text-slate-900">
                                                        <span className="text-left font-semibold text-slate-900 hover:text-red-600 transition-colors">
                                                            {log.student_name || log.student_full_name || "Unknown Student"}
                                                        </span>
                                                        <div className="text-[11px] text-slate-500 font-normal mt-0.5 flex flex-wrap items-center gap-2">
                                                            <span>Class: <strong className="text-slate-700 font-medium">{log.grade_section || log.class_name || "N/A"}</strong></span>
                                                            <span className="text-slate-300">|</span>
                                                            <span>Subject: <strong className="text-slate-700 font-medium">{log.subject_name || log.homeroom_teacher?.full_name || log.teacher_name || "N/A"}</strong></span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3.5 px-4 text-slate-700 font-semibold">{violationDisplay}</td>
                                                    
                                                    <td className="py-3.5 px-4 text-center">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold font-mono ${
                                                            totalCount >= 3 
                                                                ? 'bg-red-100 text-red-700 border border-red-200' 
                                                                : 'bg-amber-100 text-amber-700 border border-amber-200'
                                                        }`}>
                                                            {totalCount} {totalCount === 1 ? 'violation' : 'violations'}
                                                        </span>
                                                    </td>

                                                    <td className="py-3.5 px-4 text-slate-500 whitespace-nowrap">
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-slate-700">{dateTime.date}</span>
                                                            {dateTime.time && (
                                                                <span className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                                                    <Clock size={11} />
                                                                    {dateTime.time}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-10 text-slate-400 text-xs font-medium">
                                                No disciplinary history or logged infractions match the specified criteria.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Footer */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-slate-100 bg-slate-50/50 text-xs font-medium text-slate-500">
                            <div className="flex items-center gap-2">
                                <span>Rows per page:</span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs font-semibold text-slate-700 focus:outline-none cursor-pointer"
                                >
                                    {[5, 10, 20, 50].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                                <span className="ml-2 text-slate-400">
                                    Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{" "}
                                    {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                                </span>
                            </div>

                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="p-1.5 border border-slate-200 bg-white rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-white cursor-pointer"
                                >
                                    <ChevronLeft size={14} />
                                </button>

                                <span className="px-3 py-1 font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg">
                                    {currentPage} / {totalPages}
                                </span>

                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage >= totalPages}
                                    className="p-1.5 border border-slate-200 bg-white rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-white cursor-pointer"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>

                    </div>
                )}
            </div>

            {/* Modals */}
            {isModalOpen && (
                <UniformInfractionModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setSelectedInfraction(null);
                    }}
                    infractionData={selectedInfraction}
                    onSave={handleSaveInfraction}
                />
            )}

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setInfractionToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                title="Delete Infraction Log"
                message={
                    infractionToDelete 
                        ? `Are you sure you want to delete the log for ${infractionToDelete.student_name || 'this student'} (${getViolationText(infractionToDelete)})? This action cannot be undone.`
                        : "Are you sure you want to delete this infraction record?"
                }
            />
        </StudentListLayout>
    );
}